"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef } from "react";
import { ChevronRight, Layers, ToyBrick } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ItemCard } from "@/components/search/item-card";
import type { FeedFilterState, ListingItem } from "@/hooks/use-paginated-feed";
import { useDiscoverBrands, type DiscoverBrandRow } from "@/hooks/use-discover-brands";
import { useDiscoverTcgs } from "@/hooks/use-discover-tcgs";
import { usePaginatedFeed } from "@/hooks/use-paginated-feed";
import { useRouter } from "next/navigation";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemCardSkeletonRow } from "@/components/search/item-card-skeleton";
import { discoverRowCtaButtonClassName, discoverRowCtaChevronClassName } from "@/lib/discover-row-cta-button";
import type { DiscoverCategorySelection } from "@/components/feed/feed-discover-category-slider";

const DISCOVER_ROW_CAP = 48;
const DISCOVER_PAGE_SIZE = 48;

type SortOption = "price-asc" | "price-desc" | "date-asc" | "date-desc" | null;

interface DiscoverFeedProps {
  filters: FeedFilterState & { sortBy: SortOption };
  currentUserId?: string;
  refreshSignal?: number;
  onViewAllCategoryAction?: (selection: DiscoverCategorySelection) => void;
}

function CarouselRow({
  listings,
  useEagerImages,
  currentUserId,
}: {
  listings: ListingItem[];
  useEagerImages: boolean;
  currentUserId?: string;
}) {
  const router = useRouter();

  return (
    <div className="scrollbar-hide -mx-4 overflow-x-auto overscroll-x-contain px-4 pb-0.5 md:mx-0 md:px-0">
      <div className="flex w-max min-w-0 gap-3 md:gap-4">
        {listings.map((listing) => {
          const discoverTypeLabel =
            "discoverTypeLabel" in listing && listing.discoverTypeLabel
              ? listing.discoverTypeLabel
              : undefined;
          return (
            <div
              key={listing.id}
              className="w-[152px] shrink-0 min-w-0 sm:w-[168px] md:w-[180px] lg:w-[188px]"
            >
              <ItemCard
                listingId={listing.id}
                title={listing.title}
                price={listing.price}
                currency={listing.currency ?? null}
                image_urls={listing.image_urls}
                stock={listing.quantity}
                scheduledPublishAt={listing.scheduled_publish_at as string}
                listingFormat={(listing as ListingItem).listing_format ?? null}
                listingType={(listing as ListingItem).type ?? null}
                discoverTypeLabel={typeof discoverTypeLabel === "string" ? discoverTypeLabel : null}
                imageLoading={useEagerImages ? "eager" : "lazy"}
                currentUserId={currentUserId}
                sellerId={(listing as ListingItem).user_id}
                sellerName={(listing as ListingItem).sellerUsername}
                sellerAvatarUrl={(listing as ListingItem).sellerAvatarUrl ?? null}
                condition={listing.condition}
                cardDetails={(listing as ListingItem).card_details ?? null}
                description={listing.description}
                onClick={() => router.push(`/listings/${listing.id}`)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DiscoverFeed({
  refreshSignal,
  filters: _filters,
  currentUserId,
  onViewAllCategoryAction,
}: DiscoverFeedProps) {
  const tTabs = useTranslations("feedTabs");
  const tCard = useTranslations("cardCreation.typeStep");

  const { brands, isLoading: isLoadingBrands, error: brandsError } = useDiscoverBrands();
  const { tcgs, isLoading: isLoadingTcgs, error: tcgError } = useDiscoverTcgs();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const refreshInitializedRef = useRef(false);
  const rerollBuildingRef = useRef<() => void>(() => {});
  const rerollTcgRef = useRef<() => void>(() => {});

  const buildingBlockBrands = useMemo(() => {
    const accepted = new Set(["lego", "other"]);
    const rows = brands.filter((brand) => {
      const slug = brand.slug.trim().toLowerCase();
      const name = brand.name.trim().toLowerCase();
      return accepted.has(slug) || accepted.has(name);
    });
    const rank = (value: DiscoverBrandRow) => {
      const slug = value.slug.trim().toLowerCase();
      if (slug === "lego") return 0;
      if (slug === "other") return 1;
      return 10;
    };
    return rows.slice().sort((a, b) => rank(a) - rank(b) || (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  }, [brands]);

  const buildingBlockFilters = useMemo<FeedFilterState>(
    () => ({
      brands: buildingBlockBrands.map((brand) => brand.id),
      themes: _filters.themes,
      categories: _filters.categories,
      tcgs: [],
      conditions: _filters.conditions,
      gradeRange: _filters.gradeRange,
    }),
    [_filters.categories, _filters.conditions, _filters.gradeRange, _filters.themes, buildingBlockBrands]
  );

  const tradingCardFilters = useMemo<FeedFilterState>(
    () => ({
      brands: _filters.brands,
      themes: _filters.themes,
      categories: _filters.categories,
      tcgs: tcgs.map((tcg) => tcg.id),
      conditions: _filters.conditions,
      gradeRange: _filters.gradeRange,
    }),
    [tcgs, _filters.brands, _filters.categories, _filters.conditions, _filters.gradeRange, _filters.themes]
  );

  const buildingBlocksFeed = usePaginatedFeed({
    userId: currentUserId,
    mode: "discover",
    filters: buildingBlockFilters,
    sortBy: _filters.sortBy,
    limit: DISCOVER_PAGE_SIZE,
    maxItems: DISCOVER_ROW_CAP,
    enabled: buildingBlockBrands.length > 0,
    // Persist + restore across navigation (skeleton-on-back fix), same
    // mechanism as live-feed.tsx. @see lib/feed/feed-snapshot.ts
    cacheKey: "discover-bb",
  });

  const tradingCardsFeed = usePaginatedFeed({
    userId: currentUserId,
    mode: "discover",
    filters: tradingCardFilters,
    sortBy: _filters.sortBy,
    limit: DISCOVER_PAGE_SIZE,
    maxItems: DISCOVER_ROW_CAP,
    enabled: tcgs.length > 0,
    cacheKey: "discover-tcg",
  });

  rerollBuildingRef.current = buildingBlocksFeed.reroll;
  rerollTcgRef.current = tradingCardsFeed.reroll;

  const buildingBlocksListings = useMemo(() => {
    return buildingBlocksFeed.items.slice(0, DISCOVER_ROW_CAP);
  }, [buildingBlocksFeed.items]);

  const tradingCardsListings = useMemo(() => {
    return tradingCardsFeed.items.slice(0, DISCOVER_ROW_CAP);
  }, [tradingCardsFeed.items]);

  const showBuildingBlockSkeleton =
    !buildingBlocksFeed.error && (!buildingBlocksFeed.isInitialized || buildingBlocksFeed.isLoading) && buildingBlocksFeed.items.length === 0;

  const showTradingCardsSkeleton =
    !tradingCardsFeed.error && (!tradingCardsFeed.isInitialized || tradingCardsFeed.isLoading) && tradingCardsFeed.items.length === 0;

  // Same guard as live-feed.tsx: `refreshSignal` only increments on an
  // explicit user tab-switch/re-tap (see feed-page-client-state.ts), so the
  // first value observed here is always treated as a no-op — a snapshot-
  // restored back-nav mount does not reroll these rows.
  useEffect(() => {
    if (typeof refreshSignal === "undefined") {
      return;
    }
    if (!refreshInitializedRef.current) {
      refreshInitializedRef.current = true;
      return;
    }
    rerollBuildingRef.current();
    rerollTcgRef.current();
    const scrollRoot = rootRef.current?.closest("[data-feed-scroll-root]") as HTMLElement | null;
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const behavior: ScrollBehavior = prefersReducedMotion ? "auto" : "smooth";
    if (scrollRoot && typeof scrollRoot.scrollTo === "function") {
      scrollRoot.scrollTo({ top: 0, behavior });
    } else if (typeof window !== "undefined" && typeof window.scrollTo === "function") {
      window.scrollTo({ top: 0, behavior });
    }
  }, [refreshSignal]);
  const createBuildingBlocksSelection = (): DiscoverCategorySelection => ({
    categorySlug: "building-blocks",
    name: tCard("buildingBlocks"),
    liveFilters: buildingBlockFilters,
    relativeFilterMode: "brand",
    relativeFilterOptions: buildingBlockBrands.map((brand) => ({
      id: brand.id,
      name: brand.name,
      slug: brand.slug,
    })),
  });

  const createTradingCardsSelection = (): DiscoverCategorySelection => ({
    categorySlug: "trading-card-games",
    name: tCard("tradingCardGames"),
    liveFilters: tradingCardFilters,
    relativeFilterMode: "tcg",
    relativeFilterOptions: tcgs.map((tcg) => ({ id: tcg.id, name: tcg.name })),
  });

  return (
    <div ref={rootRef} className="w-full pt-3 md:pt-4">
      {brandsError ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Could not load building blocks categories.</p>
      ) : null}

      {tcgError ? (
        <p className="py-3 text-center text-sm text-muted-foreground">Could not load trading card game types.</p>
      ) : null}

      {!brandsError && isLoadingBrands && buildingBlockBrands.length === 0 ? (
        <div className="mb-8">
          <div className="mb-3 flex border-b border-border/60 pb-2.5 dark:border-white/10">
            <Skeleton className="h-7 w-48 animate-none rounded-md bg-muted dark:bg-white/5" />
          </div>
          <ItemCardSkeletonRow count={8} keyPrefix="building-blocks-sk" />
        </div>
      ) : null}

      {!tcgError && isLoadingTcgs && tcgs.length === 0 ? (
        <div className="mb-8">
          <div className="mb-3 flex border-b border-border/60 pb-2.5 dark:border-white/10">
            <Skeleton className="h-7 w-48 animate-none rounded-md bg-muted dark:bg-white/5" />
          </div>
          <ItemCardSkeletonRow count={8} keyPrefix="tcg-sk" />
        </div>
      ) : null}

      {buildingBlockBrands.length > 0 ? (
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-border/60 pb-2.5 dark:border-white/10">
            <h2 className="flex min-w-0 flex-1 items-center gap-2 text-base font-semibold tracking-tight text-foreground">
              <ToyBrick className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 truncate">{tCard("buildingBlocks")}</span>
            </h2>
            {onViewAllCategoryAction ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onViewAllCategoryAction(createBuildingBlocksSelection())}
                className={discoverRowCtaButtonClassName}
              >
                <span>{tTabs("viewAll")}</span>
                <ChevronRight className={discoverRowCtaChevronClassName} aria-hidden />
              </Button>
            ) : null}
          </div>

          {showBuildingBlockSkeleton ? (
            <ItemCardSkeletonRow count={12} keyPrefix="building-blocks-row" />
          ) : (
            <CarouselRow listings={buildingBlocksListings} useEagerImages currentUserId={currentUserId} />
          )}
        </section>
      ) : null}

      {tcgs.length > 0 ? (
        <section className="mb-8 last:mb-2">
          <div className="mb-3 flex items-center justify-between gap-3 border-b border-border/60 pb-2.5 dark:border-white/10">
            <h2 className="flex min-w-0 flex-1 items-center gap-2 text-base font-semibold tracking-tight text-foreground">
              <Layers className="h-5 w-5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 truncate">{tCard("tradingCardGames")}</span>
            </h2>
            {onViewAllCategoryAction ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onViewAllCategoryAction(createTradingCardsSelection())}
                className={discoverRowCtaButtonClassName}
              >
                <span>{tTabs("viewAll")}</span>
                <ChevronRight className={discoverRowCtaChevronClassName} aria-hidden />
              </Button>
            ) : null}
          </div>

          {showTradingCardsSkeleton ? (
            <ItemCardSkeletonRow count={12} keyPrefix="trading-cards-row" />
          ) : (
            <CarouselRow listings={tradingCardsListings} useEagerImages currentUserId={currentUserId} />
          )}
        </section>
      ) : null}
    </div>
  );
}
